import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Button,
  Typography,
  Slider,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import styled from "styled-components";
import ScheduleIcon from "@mui/icons-material/Schedule";
import FlagIcon from "@mui/icons-material/Flag";

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-input {
    padding: 0;
    height: 0;
    visibility: hidden;
  }
`;

function BulkEditDialog({
  open,
  onClose,
  selectedRows,
  onSave,
  openSnackbar,
  setRowSelection,
}) {
  const [formData, setFormData] = useState({
    qty: "",
    priority: 1,
    start_date: null,
    due_date: null,
    pce: "",
    lot_size: "",
    alignment_option: "start",
  });

  const [enabledFields, setEnabledFields] = useState({
    qty: false,
    priority: false,
    start_date: false,
    due_date: false,
    pce: false,
    lot_size: false,
    alignment_option: false,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFieldToggle = (field) => {
    setEnabledFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleAlignmentChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      handleInputChange("alignment_option", newAlignment);
    }
  };

  const handlePceChange = (e) => {
    const value = e.target.value;
    if (
      value === "" ||
      (/^[1-9]\d*$/.test(value) &&
        parseInt(value) >= 1 &&
        parseInt(value) <= 100)
    ) {
      handleInputChange("pce", value);
    }
  };

  const handleLotSizeChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^[1-9]\d*$/.test(value) && parseInt(value) >= 1)) {
      handleInputChange("lot_size", value);
    }
  };

  const handleSave = async () => {
    const updatedData = {};
    Object.keys(enabledFields).forEach((field) => {
      if (enabledFields[field]) {
        if (field === "start_date" || field === "due_date") {
          updatedData[field] = formData[field]
            ? formData[field].format("YYYY-MM-DD HH:mm:ss")
            : null;
        } else {
          updatedData[field] = formData[field];
        }
      }
    });

    if (Object.keys(updatedData).length === 0) {
      openSnackbar("수정할 항목을 선택해주세요.", "warning");
      return;
    }

    try {
      const updatePromises = selectedRows.map((row) => {
        const updateObj = {
          order_id: row.original.order_id,
          order_name: row.original.order_name,
          color: row.original.color,
          item_id: row.original.item_id,
          qty: enabledFields.qty ? Number(updatedData.qty) : row.original.qty,
          start_date: enabledFields.start_date
            ? updatedData.start_date
            : row.original.start_date,
          due_date: enabledFields.due_date
            ? updatedData.due_date
            : row.original.due_date,
          priority: enabledFields.priority
            ? updatedData.priority
            : row.original.order_priority,
          pce: enabledFields.pce ? Number(updatedData.pce) : row.original.pce,
          lot_size: enabledFields.lot_size
            ? Number(updatedData.lot_size)
            : row.original.lot_size,
          alignment_option: enabledFields.alignment_option
            ? updatedData.alignment_option
            : row.original.alignment_option || "start",
        };
        return onSave({ values: updateObj });
      });

      await Promise.all(updatePromises);
      openSnackbar(`${selectedRows.length}개 항목이 수정되었습니다.`);

      if (setRowSelection) {
        setRowSelection({});
      }

      handleClose();
    } catch (error) {
      openSnackbar("수정 중 오류가 발생했습니다.", "error");
    }
  };

  const handleClose = () => {
    setFormData({
      qty: "",
      priority: 1,
      start_date: null,
      due_date: null,
      pce: "",
      lot_size: "",
      alignment_option: "start",
    });
    setEnabledFields({
      qty: false,
      priority: false,
      start_date: false,
      due_date: false,
      pce: false,
      lot_size: false,
      alignment_option: false,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "444px",
        },
      }}
    >
      <DialogTitle
        variant="h5"
        sx={{ fontSize: "18px !important", color: "#111" }}
      >
        선택 항목 일괄 수정 ({selectedRows.length}개)
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
      >
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledFields.qty}
                onChange={() => handleFieldToggle("qty")}
              />
            }
            label="수량 수정"
            sx={{ mb: 1 }}
          />
          <TextField
            label="수량"
            type="number"
            value={formData.qty}
            onChange={(e) => handleInputChange("qty", e.target.value)}
            disabled={!enabledFields.qty}
            fullWidth
          />
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledFields.priority}
                onChange={() => handleFieldToggle("priority")}
              />
            }
            label="우선순위 수정"
            sx={{ mb: 1 }}
          />
          <StyledTextField
            label="우선순위"
            fullWidth
            disabled={!enabledFields.priority}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <Box sx={{ width: "2000%", px: 1, py: 2 }}>
                  <Slider
                    value={formData.priority}
                    onChange={(_, value) =>
                      handleInputChange("priority", value)
                    }
                    min={1}
                    max={100}
                    step={null}
                    marks={[
                      { value: 1, label: "" },
                      { value: 10, label: "" },
                      { value: 20, label: "" },
                      { value: 30, label: "" },
                      { value: 40, label: "" },
                      { value: 50, label: "" },
                      { value: 60, label: "" },
                      { value: 70, label: "" },
                      { value: 80, label: "" },
                      { value: 90, label: "" },
                      { value: 100, label: "" },
                    ]}
                    valueLabelDisplay="off"
                    disabled={!enabledFields.priority}
                    sx={{
                      "& .MuiSlider-rail": {
                        opacity: 0.3,
                      },
                      "& .MuiSlider-thumb": {
                        width: 16,
                        height: 16,
                      },
                      "& .MuiSlider-mark": {
                        backgroundColor: "currentColor",
                        height: 8,
                        width: 2,
                      },
                      "& .MuiSlider-markActive": {
                        backgroundColor: "currentColor",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 1,
                      mt: 1,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      "& .selected": {
                        fontWeight: 700,
                        color: "text.primary",
                      },
                    }}
                  >
                    {[1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((num) => (
                      <span
                        key={num}
                        className={num === formData.priority ? "selected" : ""}
                      >
                        {num}
                      </span>
                    ))}
                  </Box>
                </Box>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>

        <div style={{ display: "flex", gap: "0.3rem" }}>
          <Box sx={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledFields.pce}
                  onChange={() => handleFieldToggle("pce")}
                />
              }
              label="PCE 수정"
              sx={{ mb: 1 }}
            />
            <TextField
              label="PCE"
              value={formData.pce}
              onChange={handlePceChange}
              disabled={!enabledFields.pce}
              fullWidth
              type="number"
              inputProps={{
                step: 1,
                min: 1,
                max: 100,
              }}
              helperText="1 이상 100 이하의 정수"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledFields.lot_size}
                  onChange={() => handleFieldToggle("lot_size")}
                />
              }
              label="LOT SIZE 수정"
              sx={{ mb: 1 }}
            />
            <TextField
              label="LOT SIZE"
              value={formData.lot_size}
              onChange={handleLotSizeChange}
              disabled={!enabledFields.lot_size}
              fullWidth
              type="number"
              inputProps={{
                step: 1,
                min: 1,
              }}
              helperText="1 이상의 정수"
            />
          </Box>
        </div>

        <div style={{ display: "flex", gap: "0.3rem" }}>
          <Box sx={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledFields.start_date}
                  onChange={() => handleFieldToggle("start_date")}
                />
              }
              label="생산 시작 수정"
              sx={{ mb: 1 }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDateTimePicker
                label="시작 시간"
                value={formData.start_date}
                onChange={(date) => handleInputChange("start_date", date)}
                format="YYYY년 MM월 DD일 HH시 mm분"
                disabled={!enabledFields.start_date}
                sx={{
                  width: "100%",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.7rem",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.6rem",
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabledFields.due_date}
                  onChange={() => handleFieldToggle("due_date")}
                />
              }
              label="납기일 수정"
              sx={{ mb: 1 }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDateTimePicker
                label="납기일"
                value={formData.due_date}
                onChange={(date) => handleInputChange("due_date", date)}
                format="YYYY년 MM월 DD일 HH시 mm분"
                disabled={!enabledFields.due_date}
                sx={{
                  width: "100%",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.7rem",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.6rem",
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </div>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledFields.alignment_option}
                onChange={() => handleFieldToggle("alignment_option")}
              />
            }
            label="스케줄링 정렬 옵션 수정"
            sx={{ mb: 1 }}
          />
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
            스케줄링 정렬 옵션
          </Typography>
          <ToggleButtonGroup
            value={formData.alignment_option}
            exclusive
            onChange={handleAlignmentChange}
            aria-label="alignment option"
            fullWidth
            disabled={!enabledFields.alignment_option}
            sx={{
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 1.5,
                borderRadius: 1,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                },
              },
            }}
          >
            <ToggleButton value="start" aria-label="start alignment">
              <ScheduleIcon sx={{ mr: 1, fontSize: 18 }} />
              시작일 기준
            </ToggleButton>
            <ToggleButton value="end" aria-label="end alignment">
              <FlagIcon sx={{ mr: 1, fontSize: 18 }} />
              납기일 기준
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            {formData.alignment_option === "start"
              ? "시작일을 기준으로 스케줄링됩니다"
              : "납기일을 기준으로 역산하여 스케줄링됩니다"}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          paddingTop: "1.5rem !important",
        }}
      >
        <Button onClick={handleClose} color="inherit">
          취소
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BulkEditDialog;
